import React, { useEffect, useState } from 'react';
import { Button, Col, Divider, message, Modal, Row, Select, Spin } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Link } from 'lucide-react';
import { get, post } from '../../utils/fetch';
import styles from './JiraUserMappingModal.module.css';

type Mapping = { trelloUserId: string; bugherdUserId: string };

interface Props {
  open: boolean;
  onClose: () => void;
  organizationId: number;
  integrationId: string | null;
  projectIntegrationId: string | null;
  boardId: string | null;
  bugherdUsers: any[];
  container?: HTMLElement | (() => HTMLElement);
}

const TrelloUserMappingModal: React.FC<Props> = ({
  open,
  onClose,
  organizationId,
  integrationId,
  projectIntegrationId,
  boardId,
  bugherdUsers,
  container
}) => {
  const [loading, setLoading] = useState(false);
  const [trelloUsers, setTrelloUsers] = useState<any[]>([]);
  const [mappings, setMappings] = useState<Mapping[]>([
    { trelloUserId: '', bugherdUserId: '' }
  ]);

  useEffect(() => {
    if (open && integrationId && boardId && boardId !== 'undefined') {
      loadData();
    }
  }, [open, integrationId, boardId]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([fetchTrelloUsers(), fetchCurrentSettings()]);
    setLoading(false);
  };

  const fetchTrelloUsers = async () => {
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integrationId}&site_name=members:${boardId}`
      );
      const formattedUsers = results.map((user: any) => ({
        ...user,
        label: user.username
          ? `${user.label} (${user.username})`
          : user.label,
        originalLabel: user.label
      }));
      setTrelloUsers(formattedUsers);
    } catch (error) {
      message.error('Failed to fetch Trello users.');
    }
  };

  const fetchCurrentSettings = async () => {
    if (!projectIntegrationId || projectIntegrationId === 'undefined') {
      setMappings([{ trelloUserId: '', bugherdUserId: '' }]);
      return;
    }

    try {
      const result = await get(
        `/organizations/${organizationId}/integrations/${integrationId}/project_integrations/${projectIntegrationId}`
      );
      if (result?.settings?.user_mapping) {
        const initialMappings = Object.entries(result.settings.user_mapping).map(
          ([trelloId, bugherdId]) => ({
            trelloUserId: trelloId,
            bugherdUserId: String(bugherdId)
          })
        );
        setMappings(
          initialMappings.length > 0
            ? initialMappings
            : [{ trelloUserId: '', bugherdUserId: '' }]
        );
      } else {
        setMappings([{ trelloUserId: '', bugherdUserId: '' }]);
      }
    } catch (error) {
      console.error('Failed to fetch current settings', error);
      setMappings([{ trelloUserId: '', bugherdUserId: '' }]);
    }
  };

  const saveMappings = async (currentMappings: Mapping[]) => {
    try {
      const userMappingPayload = currentMappings.reduce((acc, mapping) => {
        if (mapping.trelloUserId && mapping.bugherdUserId) {
          acc[mapping.trelloUserId] = mapping.bugherdUserId;
        }
        return acc;
      }, {} as Record<string, string>);

      await post(
        `/organizations/${organizationId}/integrations/${integrationId}/project_integrations/save_settings?integration_code=Trello`,
        {
          project_integration_id: projectIntegrationId,
          settings: {
            user_mapping: userMappingPayload
          }
        }
      );
    } catch (error) {
      message.error('Failed to save user mappings.');
    }
  };

  const handleAddMapping = () => {
    setMappings([...mappings, { trelloUserId: '', bugherdUserId: '' }]);
  };

  const handleRemoveMapping = (index: number) => {
    const newMappings = mappings.filter((_, i) => i !== index);
    setMappings(newMappings);
    saveMappings(newMappings);
  };

  const handleMappingChange = (
    index: number,
    field: 'trelloUserId' | 'bugherdUserId',
    value: string
  ) => {
    const newMappings = [...mappings];
    newMappings[index][field] = value;
    setMappings(newMappings);

    if (newMappings[index].trelloUserId && newMappings[index].bugherdUserId) {
      saveMappings(newMappings);
    }
  };

  const normalize = (value?: string) => value?.toLowerCase().trim() || '';

  const handleAutoMap = () => {
    const autoPairs: Mapping[] = [];

    trelloUsers.forEach(trelloUser => {
      const trelloNames = [
        trelloUser.fullName,
        trelloUser.username,
        trelloUser.originalLabel,
        trelloUser.label
      ]
        .map(normalize)
        .filter(Boolean);

      const matchedBugherdUser = bugherdUsers.find(bugherdUser => {
        const emailLocalPart = bugherdUser.email?.split('@')[0];
        const bugherdNames = [
          bugherdUser.display_name,
          bugherdUser.name,
          bugherdUser.email,
          emailLocalPart
        ]
          .map(normalize)
          .filter(Boolean);

        return bugherdNames.some(name => trelloNames.includes(name));
      });

      if (matchedBugherdUser) {
        autoPairs.push({
          trelloUserId: trelloUser.value,
          bugherdUserId: String(matchedBugherdUser.id)
        });
      }
    });

    if (autoPairs.length > 0) {
      setMappings(autoPairs);
      saveMappings(autoPairs);
      message.success(`Successfully mapped ${autoPairs.length} users.`);
    } else {
      message.info('No matching users found by name or username.');
    }
  };

  const bugherdUserOptions = bugherdUsers.map(user => ({
    label: user.display_name || user.email,
    value: String(user.id)
  }));

  const renderMappingList = () => (
    <div className={styles.mappingList}>
      <div className={styles.mappingRowsContainer}>
        <Row gutter={16} className={styles.headerRow}>
          <Col span={10}>
            <strong>BugHerd User</strong>
          </Col>
          <Col span={2}></Col>
          <Col span={10}>
            <strong>Trello Member</strong>
          </Col>
          <Col span={2}></Col>
        </Row>
        {mappings.map((mapping, index) => (
          <Row
            key={index}
            gutter={16}
            className={styles.mappingRow}
            align="middle"
          >
            <Col span={10}>
              <Select
                showSearch
                style={{ width: '100%' }}
                placeholder="Search BugHerd user"
                optionFilterProp="label"
                value={mapping.bugherdUserId || undefined}
                onChange={value =>
                  handleMappingChange(index, 'bugherdUserId', value)
                }
                options={bugherdUserOptions}
              />
            </Col>
            <Col span={2} style={{ textAlign: 'center' }}>
              <Link size={16} className={styles.linkIcon} />
            </Col>
            <Col span={10}>
              <Select
                showSearch
                style={{ width: '100%' }}
                placeholder="Search Trello member"
                optionFilterProp="label"
                value={mapping.trelloUserId || undefined}
                onChange={value =>
                  handleMappingChange(index, 'trelloUserId', value)
                }
                options={trelloUsers}
              />
            </Col>
            <Col span={2}>
              <Button
                type="text"
                icon={<MinusCircleOutlined />}
                onClick={() => handleRemoveMapping(index)}
                danger
              />
            </Col>
          </Row>
        ))}
        <div className={styles.addMapping}>
          <Button type="link" onClick={handleAddMapping} icon={<PlusOutlined />}>
            Sync another user
          </Button>
        </div>
      </div>
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <Divider plain>or</Divider>
        <Button
          type="primary"
          size="large"
          onClick={handleAutoMap}
          style={{ padding: '0 40px' }}
        >
          Automap BugHerd to Trello users
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      title="Manage users between BugHerd and Trello"
      open={open}
      onCancel={onClose}
      width={700}
      footer={null}
      getContainer={container}
    >
      <div className={styles.modalContent}>
        <p>
          This will allow you to match users that are assigned on tasks across
          BugHerd and Trello.
        </p>

        {loading ? (
          <div className={styles.loader}>
            <Spin />
          </div>
        ) : (
          renderMappingList()
        )}
      </div>
    </Modal>
  );
};

export default TrelloUserMappingModal;
